// AIBL WebSocket Service - Real-time updates
// Connects to WebSocket when available, falls back to smart polling

const WS_BASE = 'wss://api.aibl.dev';
const API_BASE = 'https://api.aibl.dev';

// Event types
export const WS_EVENTS = {
  ACTIVITY: 'activity',
  SAFE_TX: 'safe_transaction',
  AGENT_STATUS: 'agent_status',
  PLUGIN_UPDATE: 'plugin_update',
  WALLET_UPDATE: 'wallet_update',
  CONNECTION: 'connection',
};

class AIBLWebSocket {
  constructor() {
    this.ws = null;
    this.token = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnected = false;
    this.usePolling = true; // Start with polling, switch to WS when available
    this.pollingIntervals = new Map();
    this.lastData = new Map(); // Cache for deduplication
  }

  // Initialize with auth token
  init(token) {
    this.token = token;
    if (!this.usePolling) {
      this.connect();
    }
    return this;
  }

  // Connect to WebSocket
  connect() {
    if (!this.token || this.usePolling) return;

    try {
      this.ws = new WebSocket(`${WS_BASE}/v1/ws?token=${this.token}`);

      this.ws.onopen = () => {
        console.log('[AIBL WS] Connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.emit(WS_EVENTS.CONNECTION, { connected: true });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (err) {
          console.error('[AIBL WS] Parse error:', err);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[AIBL WS] Error:', error);
        // Fall back to polling
        this.usePolling = true;
      };

      this.ws.onclose = () => {
        console.log('[AIBL WS] Disconnected');
        this.isConnected = false;
        this.emit(WS_EVENTS.CONNECTION, { connected: false });
        this.attemptReconnect();
      };
    } catch (err) {
      console.error('[AIBL WS] Connection failed:', err);
      this.usePolling = true;
    }
  }

  // Handle incoming WebSocket message
  handleMessage(data) {
    const { type, payload } = data;
    if (type && this.listeners.has(type)) {
      this.emit(type, payload);
    }
  }

  // Attempt to reconnect
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[AIBL WS] Max reconnect attempts reached, switching to polling');
      this.usePolling = true;
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    console.log(`[AIBL WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (!this.isConnected) {
        this.connect();
      }
    }, delay);
  }

  // Subscribe to event type
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  // Emit event to listeners
  emit(eventType, data) {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error('[AIBL WS] Callback error:', err);
        }
      });
    }
  }

  // Send message through WebSocket
  send(type, payload) {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify({ type, payload }));
    }
  }

  // Start polling for a specific data type
  startPolling(eventType, fetchFn, interval = 10000) {
    // Don't poll if WebSocket is connected
    if (this.isConnected && !this.usePolling) return;

    // Clear existing interval
    this.stopPolling(eventType);

    // Fetch immediately
    this.pollOnce(eventType, fetchFn);

    // Set up interval
    const intervalId = setInterval(() => {
      this.pollOnce(eventType, fetchFn);
    }, interval);

    this.pollingIntervals.set(eventType, intervalId);
  }

  // Poll once
  async pollOnce(eventType, fetchFn) {
    try {
      const data = await fetchFn();
      
      // Deduplicate - only emit if data changed
      const lastDataStr = this.lastData.get(eventType);
      const newDataStr = JSON.stringify(data);
      
      if (lastDataStr !== newDataStr) {
        this.lastData.set(eventType, newDataStr);
        this.emit(eventType, data);
      }
    } catch (err) {
      console.error(`[AIBL WS] Polling error for ${eventType}:`, err);
    }
  }

  // Stop polling for a specific data type
  stopPolling(eventType) {
    const intervalId = this.pollingIntervals.get(eventType);
    if (intervalId) {
      clearInterval(intervalId);
      this.pollingIntervals.delete(eventType);
    }
  }

  // Stop all polling
  stopAllPolling() {
    this.pollingIntervals.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this.pollingIntervals.clear();
  }

  // Disconnect
  disconnect() {
    this.stopAllPolling();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.token = null;
    this.lastData.clear();
  }

  // Check if connected (WS or polling active)
  get connected() {
    return this.isConnected || this.pollingIntervals.size > 0;
  }
}

// Singleton instance
const aiblWebSocket = new AIBLWebSocket();

export default aiblWebSocket;

// React hook for WebSocket subscriptions
export function useWebSocket(eventType, callback, deps = []) {
  const { useEffect, useRef } = require('react');
  const callbackRef = useRef(callback);
  
  // Update callback ref
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const unsubscribe = aiblWebSocket.on(eventType, (data) => {
      callbackRef.current(data);
    });

    return () => {
      unsubscribe();
    };
  }, [eventType, ...deps]);
}

// React hook for activity feed
export function useActivityFeed(token, agentId = null) {
  const { useState, useEffect, useCallback } = require('react');
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    if (!token) return [];
    try {
      const res = await fetch(`${API_BASE}/v1/dashboard/activity`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        let items = data.data?.activity || [];
        // Filter by agent if specified
        if (agentId) {
          items = items.filter(a => a.agentId === agentId);
        }
        return items;
      }
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    }
    return [];
  }, [token, agentId]);

  useEffect(() => {
    if (!token) return;

    // Initialize WebSocket
    aiblWebSocket.init(token);

    // Subscribe to activity events
    const unsubscribe = aiblWebSocket.on(WS_EVENTS.ACTIVITY, (data) => {
      setActivity(data);
      setLoading(false);
    });

    // Start polling (will be used until WebSocket is available)
    aiblWebSocket.startPolling(WS_EVENTS.ACTIVITY, fetchActivity, 15000);

    // Initial fetch
    fetchActivity().then(data => {
      setActivity(data);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      aiblWebSocket.stopPolling(WS_EVENTS.ACTIVITY);
    };
  }, [token, agentId, fetchActivity]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchActivity().then(data => {
      setActivity(data);
      setLoading(false);
    });
  }, [fetchActivity]);

  return { activity, loading, refresh };
}

// React hook for Safe transactions
export function useSafeTransactions(token, agentId, status = 'PROPOSED') {
  const { useState, useEffect, useCallback } = require('react');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    if (!token || !agentId) return [];
    try {
      const res = await fetch(`${API_BASE}/v1/safe/${agentId}/transactions?status=${status}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        return data.data?.transactions || [];
      }
    } catch (err) {
      console.error('Failed to fetch Safe transactions:', err);
    }
    return [];
  }, [token, agentId, status]);

  useEffect(() => {
    if (!token || !agentId) return;

    // Initialize WebSocket
    aiblWebSocket.init(token);

    // Subscribe to Safe transaction events
    const unsubscribe = aiblWebSocket.on(WS_EVENTS.SAFE_TX, (data) => {
      // Filter for this agent
      if (data.agentId === agentId) {
        setTransactions(prev => {
          // Update or add transaction
          const idx = prev.findIndex(tx => tx.id === data.id);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = data;
            return updated;
          }
          return [data, ...prev];
        });
      }
    });

    // Start polling for Safe transactions (shorter interval for time-sensitive data)
    aiblWebSocket.startPolling(`safe_tx_${agentId}`, fetchTransactions, 10000);

    // Initial fetch
    fetchTransactions().then(data => {
      setTransactions(data);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      aiblWebSocket.stopPolling(`safe_tx_${agentId}`);
    };
  }, [token, agentId, status, fetchTransactions]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchTransactions().then(data => {
      setTransactions(data);
      setLoading(false);
    });
  }, [fetchTransactions]);

  return { transactions, loading, refresh };
}

// React hook for agent status
export function useAgentStatus(token, agentId) {
  const { useState, useEffect, useCallback } = require('react');
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAgent = useCallback(async () => {
    if (!token || !agentId) return null;
    try {
      const res = await fetch(`${API_BASE}/v1/agents/${agentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        return data.data?.agent || data.data;
      }
    } catch (err) {
      console.error('Failed to fetch agent:', err);
    }
    return null;
  }, [token, agentId]);

  useEffect(() => {
    if (!token || !agentId) return;

    // Initialize WebSocket
    aiblWebSocket.init(token);

    // Subscribe to agent status events
    const unsubscribe = aiblWebSocket.on(WS_EVENTS.AGENT_STATUS, (data) => {
      if (data.id === agentId) {
        setAgent(data);
      }
    });

    // Start polling for agent status (longer interval)
    aiblWebSocket.startPolling(`agent_${agentId}`, fetchAgent, 30000);

    // Initial fetch
    fetchAgent().then(data => {
      setAgent(data);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      aiblWebSocket.stopPolling(`agent_${agentId}`);
    };
  }, [token, agentId, fetchAgent]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchAgent().then(data => {
      setAgent(data);
      setLoading(false);
    });
  }, [fetchAgent]);

  return { agent, loading, refresh };
}
